import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api, imageUrl, formatPrice, formatApiErrorDetail } from "@/lib/api";
import { Truck, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, total, hydrated } = useCart();
  const { payments_enabled, ready: settingsReady } = useSettings();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postcode: "",
    country: "Australia",
  });
  const [submitting, setSubmitting] = useState(false);

  if (!hydrated || !settingsReady) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center label-eyebrow text-[#5B5653]">
        Loading…
      </div>
    );
  }

  if (!payments_enabled) {
    const item = items[0];
    const subject = encodeURIComponent(item ? `Enquiry — ${item.title}` : "Enquiry — purchase");
    const body = encodeURIComponent(
      item
        ? `Hi Colleen,\n\nI'd like to enquire about purchasing "${item.title}" (${item.size}, ${formatPrice(item.price)}).\n\nThanks,\n`
        : "Hi Colleen,\n\nI'd like to enquire about purchasing one of your paintings.\n\nThanks,\n"
    );
    return (
      <div
        data-testid="checkout-disabled"
        className="max-w-2xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center"
      >
        <p className="label-eyebrow">Online checkout</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2D2926] mt-3 tracking-tight">
          Coming soon
        </h1>
        <p className="text-[#5B5653] leading-relaxed mt-6 max-w-md mx-auto">
          We're putting the final touches on the secure online checkout. Until then, every
          painting is still very much available — Colleen will personally arrange your purchase
          and free Australia-wide delivery by email.
        </p>
        <a
          href={`mailto:colleen.prosser@outlook.com?subject=${subject}&body=${body}`}
          data-testid="enquire-purchase-btn"
          className="btn-primary mt-10 inline-flex"
        >
          <Mail className="w-4 h-4 mr-2" />
          Enquire to Purchase
        </a>
        <p className="text-xs text-[#5B5653] mt-6">
          Or write to <a className="text-[#E25C46]" href="mailto:colleen.prosser@outlook.com">colleen.prosser@outlook.com</a>
        </p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
        <p className="label-eyebrow">Empty Cart</p>
        <h2 className="font-serif text-3xl mt-4 text-[#2D2926]">Nothing to check out</h2>
        <Link to="/" className="btn-primary mt-8 inline-flex">
          Browse Collection
        </Link>
      </div>
    );
  }

  // For simplicity: checkout one artwork at a time (each piece is one-of-one)
  const item = items[0];

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/checkout", {
        product_id: item.id,
        origin_url: window.location.origin,
        ...form,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="checkout-page"
      className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-16"
    >
      <div className="lg:col-span-7">
        <p className="label-eyebrow">Checkout</p>
        <h1 className="font-serif text-4xl md:text-5xl font-light text-[#2D2926] mt-3 tracking-tight">
          Delivery Details
        </h1>
        <p className="text-[#5B5653] mt-4 text-sm flex items-center gap-2">
          <Truck className="w-4 h-4 text-[#3E6649]" /> Free delivery across Australia.
        </p>

        <form
          data-testid="checkout-form"
          onSubmit={handleSubmit}
          className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8"
        >
          <Field
            label="Full Name"
            name="customer_name"
            value={form.customer_name}
            onChange={handleChange}
            required
            testid="checkout-name"
          />
          <Field
            label="Email"
            name="customer_email"
            type="email"
            value={form.customer_email}
            onChange={handleChange}
            required
            testid="checkout-email"
          />
          <Field
            label="Phone"
            name="customer_phone"
            type="tel"
            value={form.customer_phone}
            onChange={handleChange}
            required
            testid="checkout-phone"
          />
          <Field
            label="Address Line 1"
            name="address_line1"
            value={form.address_line1}
            onChange={handleChange}
            required
            className="md:col-span-2"
            testid="checkout-address1"
          />
          <Field
            label="Address Line 2 (optional)"
            name="address_line2"
            value={form.address_line2}
            onChange={handleChange}
            className="md:col-span-2"
            testid="checkout-address2"
          />
          <Field
            label="City / Suburb"
            name="city"
            value={form.city}
            onChange={handleChange}
            required
            testid="checkout-city"
          />
          <Field
            label="State"
            name="state"
            value={form.state}
            onChange={handleChange}
            required
            testid="checkout-state"
          />
          <Field
            label="Postcode"
            name="postcode"
            value={form.postcode}
            onChange={handleChange}
            required
            testid="checkout-postcode"
          />
          <Field
            label="Country"
            name="country"
            value={form.country}
            onChange={handleChange}
            required
            testid="checkout-country"
          />
          <div className="md:col-span-2 pt-4">
            <button
              type="submit"
              data-testid="pay-btn"
              disabled={submitting}
              className="btn-primary w-full"
            >
              {submitting ? "Redirecting…" : `Pay ${formatPrice(total)} — Continue to Stripe`}
            </button>
          </div>
        </form>
      </div>

      <aside className="lg:col-span-5">
        <div className="border border-[#E3DDD7] bg-[#EFEBE6]/40 p-8 sticky top-28">
          <p className="label-eyebrow mb-6">Order Summary</p>
          <div className="flex gap-4">
            <div className="w-24 h-28 bg-[#EFEBE6] overflow-hidden flex-shrink-0">
              <img
                src={imageUrl(item.image_path)}
                alt={item.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <p className="font-serif text-xl text-[#2D2926]">{item.title}</p>
              <p className="text-xs text-[#5B5653] mt-1">{item.size}</p>
              <p className="text-sm text-[#2D2926] mt-3">{formatPrice(item.price)}</p>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-[#E3DDD7] space-y-3 text-sm">
            <div className="flex justify-between text-[#5B5653]">
              <span>Subtotal</span>
              <span>{formatPrice(total)}</span>
            </div>
            <div className="flex justify-between text-[#5B5653]">
              <span>Delivery</span>
              <span>Free</span>
            </div>
            <div className="flex justify-between text-[#2D2926] pt-3 border-t border-[#E3DDD7]">
              <span className="label-eyebrow">Total</span>
              <span className="font-serif text-2xl">{formatPrice(total)}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, className = "", testid }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-eyebrow block mb-2">{label}</span>
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="input-line"
      />
    </label>
  );
}
